/**
 * Encompass Contact Card
 *
 * Displays a single contact type from an Encompass Investor Template.
 * Shows company name, contact details, and address information.
 */

import React from 'react';
import {
  Building2,
  User,
  MapPin,
  Phone,
  Mail,
  Globe,
  FileText,
  Printer
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CONTACT_TYPE_LABELS, type EncompassTemplateContact } from '@/types/investor-encompass';

interface EncompassContactCardProps {
  contact: EncompassTemplateContact;
}

export const EncompassContactCard: React.FC<EncompassContactCardProps> = ({
  contact
}) => {
  const hasAddress =
    contact.addressStreet1 || contact.city || contact.state || contact.zip;

  const label = CONTACT_TYPE_LABELS[contact.contactType] || contact.contactType;

  // Format address for display
  const formatAddress = () => {
    const parts: string[] = [];
    if (contact.addressStreet1) {
parts.push(contact.addressStreet1);
}
    if (contact.addressStreet2) {
parts.push(contact.addressStreet2);
}
    const cityStateZip = [
      contact.city,
      contact.state,
      contact.zip
    ].filter(Boolean).join(', ');
    if (cityStateZip) {
parts.push(cityStateZip);
}
    return parts;
  };

  // Helper for missing field display
  const MissingField = ({ label: fieldLabel }: { label: string }) => (
    <span className="text-red-600 text-xs italic">Missing {fieldLabel}</span>
  );

  // Full view
  return (
    <Card>
      <CardHeader className="px-3.5 pt-2.5 pb-2">
        <CardTitle className="text-sm font-medium">{label}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 px-3.5 py-2.5">
        {/* Company / Investor Name */}
        <div className="flex items-start gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
          <div>
            {contact.investorName ? (
              <>
                <div className="text-sm font-medium">{contact.investorName}</div>
                {contact.department && (
                  <div className="text-xs text-muted-foreground">{contact.department}</div>
                )}
              </>
            ) : (
              <MissingField label="company name" />
            )}
          </div>
        </div>

        {/* Contact Person */}
        <div className="flex items-start gap-2">
          <User className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
          <div>
            {contact.contactName ? (
              <>
                <div className="text-sm">{contact.contactName}</div>
                {contact.attentionTo && (
                  <div className="text-xs text-muted-foreground">
                    Attn: {contact.attentionTo}
                  </div>
                )}
              </>
            ) : (
              <MissingField label="contact name" />
            )}
          </div>
        </div>

        {/* Address */}
        <div className="flex items-start gap-2">
          <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
          <div className="text-sm">
            {hasAddress ? (
              formatAddress().map((line, i) => (
                <div key={i}>{line}</div>
              ))
            ) : (
              <MissingField label="address" />
            )}
          </div>
        </div>

        {/* Phone */}
        <div className="flex items-start gap-2">
          <Phone className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
          <div className="text-sm">
            {contact.phone ? (
              <div>{contact.phone}</div>
            ) : (
              <MissingField label="phone" />
            )}
            {contact.fax && (
              <div className="flex items-center gap-1 text-muted-foreground">
                <Printer className="h-3 w-3" />
                <span>{contact.fax}</span>
              </div>
            )}
          </div>
        </div>

        {/* Email */}
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
          {contact.email ? (
            <a
              href={`mailto:${contact.email}`}
              className="text-sm text-primary hover:underline truncate"
            >
              {contact.email}
            </a>
          ) : (
            <MissingField label="email" />
          )}
        </div>

        {/* Website - only show if present */}
        {contact.website && (
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
            <a
              href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:underline truncate"
            >
              {contact.website}
            </a>
          </div>
        )}

        {/* Notes - only show if present */}
        {contact.notes && (
          <div className="flex items-start gap-2">
            <FileText className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <div className="text-sm text-muted-foreground">{contact.notes}</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
